<!-- eslint-disable no-debugger -->
<template>
  <!-- eslint-disable max-len -->
  <div>
    <Banner
      v-if="showOutageBanner"
      class="c-banner--alert"
      role="alert"
    >
      <span class="font-bold block">
        THE USPS CHANGE OF ADDRESS<sup>®</sup> WEBSITE IS TEMPORARILY UNAVAILABLE.
      </span>
      We are currently unable to service your online Change-of-Address.
      We apologize for the inconvenience, please try again later.
    </Banner>
    <Banner
      v-if="showUpcomingMaintenanceBanner"
      class="c-banner--alert"
      role="alert"
    >
      {{ showUpcomingMaintenanceBanner }}
    </Banner>
    <main
      id="main"
      ref="main"
      class="disclaimer-desktop"
    >
      <header
        id="hero"
        class="disclaimer-desktop__header--choice"
      >
        <div class="u-container choice__hero--container">
          <h1 class="choice__hero--header">
            USPS Change-of-Address<sup>®</sup>
          </h1>
          <p class="choice__selections--paragraph">
            You’re in the right place. We help millions
            of people change their address and more each year.
          </p>
        </div>
      </header>
      <div
        id="value-props"
        class="bg-gray-200"
      >
        <section id="value-props-header">
          Plus, keep an eye out for additional services that may be available
        </section>
        <div class="disclaimer-desktop__cta u-container py-6">
          <section class="disclaimer-desktop__cta-choice-item">
            <img
              class="disclaimer-desktop__cta-icon"
              loading="lazy"
              :src="require('../../assets/choice/icon_informed_delivery.svg')"
              alt="Informed delivery"
            >
            <div class="disclaimer-desktop__cta-text">
              <h2 class="text-xl font-bold">
                Informed Delivery
              </h2>
              <p class="text-base">
                Sign up for this free service to digitally preview
                incoming mail and track and manage packages.
              </p>
            </div>
          </section>
          <section class="disclaimer-desktop__cta-choice-item">
            <img
              class="disclaimer-desktop__cta-icon"
              loading="lazy"
              :src="require('../../assets/choice/icon_mail_forwarding.svg')"
              alt="MYMOVE"
            >
            <div class="disclaimer-desktop__cta-text">
              <h2 class="text-xl font-bold">
                Extended Mail Forwarding
              </h2>
              <p class="text-base">
                Extend the mail forwarding service for an
                additional 6, 12 or 18 months.
              </p>
            </div>
          </section>
        </div>
      </div>
      <form
        ref="form"
        novalidate
        class="u-container pb-0"
        @submit.prevent="validate(); analytics.form.submitted();"
      >
        <IndividualProgressBar
          role="progressbar"
          :aria-valuenow="progressStep"
          :aria-valuemin="0"
          :aria-valuemax="1"
        />
        <section id="exp-inline-choice" />
        <div class="c-hairline" />
        <section
          id="get-started"
          ref="getStarted"
          class="flex py-8"
        >
          <div class="w-1/2 max:pr-xl">
            <h3
              id="step-heading"
              ref="stepHeading"
              class="mb-4"
            >
              <span class="font-bold">
                Who is moving?
              </span>
            </h3>
            <div class="flex flex-no-wrap">
              <InputRadio
                ref="individual"
                v-model="moverType"
                form-value="INDIVIDUAL"
                name="mover-type"
                class="mr-6"
                button
                :disabled="ivsProofingCompleted || disableNonBusinessMover"
                @input="analytics.click.moverType('INDIVIDUAL')"
              >
                <template v-slot:prelabel>
                  <SvgIndividual
                    class="mb-5 mx-auto"
                  />
                </template>
                Individual
              </InputRadio>
              <InputRadio
                ref="family"
                v-model="moverType"
                form-value="FAMILY"
                name="mover-type"
                button
                class="mr-6"
                :disabled="ivsProofingCompleted || disableNonBusinessMover"
                @input="analytics.click.moverType('FAMILY')"
              >
                <template v-slot:prelabel>
                  <SvgFamily
                    class="mb-5 mx-auto"
                  />
                </template>
                Family
              </InputRadio>
              <InputRadio
                ref="business"
                v-model="moverType"
                form-value="BUSINESS"
                name="mover-type"
                button
                :disabled="ivsProofingCompleted"
                @input="analytics.click.moverType('BUSINESS')"
              >
                <template v-slot:prelabel>
                  <SvgBusiness
                    class="mb-5 mx-auto"
                  />
                </template>
                Business
              </InputRadio>
            </div>
            <p
              v-if="errors.moverType.hasError"
              id="moverType-error"
              class="errors text-red-200 text-sm font-bold"
            >
              {{ errors.moverType.message }}
            </p>
            <template v-if="moverType === 'FAMILY'">
              <fieldset
                ref="familySameLastName"
                class="mt-6 flex flex-row"
                aria-labelledby="family-same-last-name"
                @keyup.tab="$refs.familyRemaining.focus();
                            familyRemaining='NO'"
              >
                <p
                  id="family-same-last-name"
                  class="flex-auto c-input-label mr-3"
                >
                  Do all individuals moving from this address share the same last name?
                </p>
                <InputRadio
                  v-model="familySameLastName"
                  form-value="YES"
                  name="family-same-last-name"
                  class="flex-shrink"
                  :has-error="errors.familySameLastName.hasError"
                  :disabled="ivsProofingCompleted"
                  @input="familySameLastNameHandle('YES'); analytics.input.familySameLastName('YES')"
                >
                  Yes
                </InputRadio>
                <InputRadio
                  v-model="familySameLastName"
                  form-value="NO"
                  name="family-same-last-name"
                  class="flex-shrink"
                  :has-error="errors.familySameLastName.hasError"
                  :disabled="ivsProofingCompleted"
                  @input="familySameLastNameHandle('NO'); analytics.input.familySameLastName('NO')"
                >
                  No
                </InputRadio>
              </fieldset>
              <p
                :class="{ 'errors': errors.familySameLastName.hasError }"
                class="c-input-helper"
              >
                {{ errors.familySameLastName.message }}
              </p>
              <fieldset
                ref="familyRemaining"
                class="mt-3 flex flex-row"
                aria-labelledby="family-remaining"
              >
                <p
                  id="family-remaining"
                  class="flex-auto c-input-label mr-3"
                >
                  Is anyone with this same last name remaining at the address?
                </p>
                <InputRadio
                  v-model="familyRemaining"
                  form-value="YES"
                  name="family-remaining"
                  class="flex-shrink"
                  tabindex="1"
                  :has-error="errors.familyRemaining.hasError"
                  :disabled="ivsProofingCompleted"
                  @input="familyRemainingHandle('YES'); analytics.input.familyRemaining('YES')"
                >
                  Yes
                </InputRadio>
                <InputRadio
                  v-model="familyRemaining"
                  form-value="NO"
                  name="family-remaining"
                  class="flex-shrink"
                  tabindex="0"
                  :has-error="errors.familyRemaining.hasError"
                  :disabled="ivsProofingCompleted"
                  @input="familyRemainingHandle('NO'); analytics.input.familyRemaining('NO')"
                >
                  No
                </InputRadio>
              </fieldset>
              <p
                :class="{ 'errors': errors.familyRemaining.hasError }"
                class="c-input-helper"
              >
                {{ errors.familyRemaining.message }}
              </p>
            </template>
            <template v-if="moverType === 'BUSINESS'">
              <fieldset
                ref="businessWholeCompany"
                aria-labelledby="business-wholeCompany"
                class="mt-6 flex flex-row"
              >
                <p
                  id="business-wholeCompany"
                  class="flex-auto c-input-label"
                >
                  Is the ENTIRE business moving?
                </p>
                <InputRadio
                  v-model="businessWholeCompany"
                  form-value="YES"
                  name="business-whole-company"
                  class="flex-shrink"
                  :has-error="errors.businessWholeCompany.hasError"
                  @input="businessWholeCompanyHandle('YES'); analytics.input.businessWholeCompany('YES')"
                >
                  Yes
                </InputRadio>
                <InputRadio
                  v-model="businessWholeCompany"
                  form-value="NO"
                  name="business-whole-company"
                  class="flex-shrink"
                  :has-error="errors.businessWholeCompany.hasError"
                  :disabled="disableNonBusinessMover"
                  @input="businessWholeCompanyHandle('NO'); analytics.input.businessWholeCompany('NO')"
                >
                  No
                </InputRadio>
              </fieldset>
              <p
                :class="{ 'errors': errors.businessWholeCompany.hasError }"
                class="c-input-helper"
              >
                {{ errors.businessWholeCompany.message }}
              </p>
              <div v-if="moverType === 'BUSINESS' && enhancedBusinessFlowEnabled">
                <fieldset
                  ref="multipleAddresses"
                  aria-labelledby="multiple-addresses"
                  class="flex flex-row"
                >
                  <p
                    id="multiple-addresses"
                    class="flex-auto c-input-label"
                  >
                    Does the business have multiple addresses?
                  </p>
                  <InputRadio
                    v-model="multipleAddresses"
                    form-value="YES"
                    name="multiple-addresses"
                    class="flex-shrink"
                    :has-error="errors.multipleAddresses.hasError"
                    @input="analytics.input.businessMultipleAddresses('YES')"
                  >
                    Yes
                  </InputRadio>
                  <InputRadio
                    v-model="multipleAddresses"
                    form-value="NO"
                    name="multiple-addresses"
                    class="flex-shrink"
                    :has-error="errors.multipleAddresses.hasError"
                    :disabled="disableNonBusinessMover"
                    @input="analytics.input.businessMultipleAddresses('NO')"
                  >
                    No
                  </InputRadio>
                </fieldset>
                <p
                  :class="{ 'errors': errors.multipleAddresses.hasError }"
                  class="c-input-helper"
                >
                  {{ errors.multipleAddresses.message }}
                </p>
              </div>
            </template>
          </div>
          <div class="w-1/2 pl-xl">
            <h3
              ref="mover-header"
              class="font-bold mb-4"
            >
              More Information
            </h3>
            <template v-if="moverType === ''">
              <p ref="mover-paragraph">
                Choose an option to let us know which type of move
                you're making with this Change-of-Address. If you're
                unsure, select an option for more information about
                that type of move.
              </p>
            </template>
            <template v-if="moverType === 'INDIVIDUAL'">
              <p><strong>Individual</strong></p>
              <p>
                Choose this option if one person is moving. If you
                receive mail by more than one name (i.e. maiden name or nickname),
                complete a separate form for each name.
              </p>
            </template>
            <template v-if="moverType === 'FAMILY'">
              <p><strong>Family</strong></p>
              <p class="mb-5">
                Choose this option and complete one form if everyone in
                your family has the same last name. If not all family
                members are moving, OR if some family members have different
                last names, choose the Individual option and submit a separate
                order for each mover.
              </p>
              <p>
                <strong>Note: Filing a Family Change-of-Address means
                  EVERYONE in the household with the same last name will have
                  their mail forwarded to the address specified.</strong>
              </p>
            </template>
            <template v-if="moverType === 'BUSINESS'">
              <p><strong>Business</strong></p>
              <p class="mb-5">
                Choose this option if your business is moving. Individual or
                Family orders that include a business address as the old address
                will not be processed. Existing or former employees cannot have
                their "personal" mail forwarded from a business.
              </p>
              <p>
                <strong>Note: All mail sent to the business name entered
                  below will be forwarded to the address specified.</strong>
              </p>
            </template>
          </div>
        </section>
        <div class="c-hairline" />
        <section class="flex py-8">
          <!-- Individual/Family Contact Information -->
          <div class="w-1/2 max:pr-xl">
            <h3
              ref="filler-heading"
              class="font-bold mb-4"
            >
              What's your contact information?
            </h3>
            <!-- Business Specific Inputs -->
            <template v-if="moverType === 'BUSINESS' && multipleAddresses !== 'YES'">
              <InputText
                ref="businessName"
                v-model.trim="businessName"
                label="Business Name"
                placeholder="Business Name"
                name="business-name"
                class="mt-5"
                :has-error="errors.businessName.hasError"
                @blur="analytics.input.businessName"
                @input="errors.businessName.hasError = false"
              >
                <span
                  v-if="errors.businessName.hasError"
                  slot="helper"
                >
                  {{ errors.businessName.message }}
                </span>
              </InputText>
              <template v-if="flagBusinessAliasEnabled && showBusinessAlias">
                <div class="c-hairline mt-5 mb-5" />
                <BusinessAlias
                  v-if="enhancedBusinessAliasesEnabled"
                  ref="businessAliases"
                  :value="businessAliases"
                  :field-error="fieldErrors"
                  :analytics="analytics"
                  :business-name="businessName"
                  :initial-alias-count="enhancedBusinessAliasesInitialCount"
                  :aliases-increment="enhancedBusinessAliasesIncrementCount"
                  :max-alias-count="enhancedBusinessAliasesMaxCount"
                />

                <template v-if="!enhancedBusinessAliasesEnabled">
                  <InputText
                    ref="businessAlias1"
                    v-model.trim="businessAlias1"
                    label="Business Alias 1"
                    placeholder="Business Alias 1"
                    name="business-alias-1"
                    class="mt-5"
                    :has-error="errors.businessAlias1.hasError"
                    @blur="analytics.input.businessAlias('1')"
                  >
                    <span
                      v-if="errors.businessAlias1.hasError"
                      slot="helper"
                    >
                      {{ errors.businessAlias1.message }}
                    </span>
                  </InputText>
                  <InputText
                    ref="businessAlias2"
                    v-model.trim="businessAlias2"
                    label="Business Alias 2"
                    placeholder="Business Alias 2"
                    name="business-alias-2"
                    class="mt-5"
                    :has-error="errors.businessAlias2.hasError"
                    @blur="analytics.input.businessAlias('2')"
                  >
                    <span
                      v-if="errors.businessAlias2.hasError"
                      slot="helper"
                    >
                      {{ errors.businessAlias2.message }}
                    </span>
                  </InputText>
                  <InputText
                    ref="businessAlias3"
                    v-model.trim="businessAlias3"
                    label="Business Alias 3"
                    placeholder="Business Alias 3"
                    name="business-alias-3"
                    class="mt-5"
                    :has-error="errors.businessAlias3.hasError"
                    @blur="analytics.input.businessAlias('3')"
                  >
                    <span
                      v-if="errors.businessAlias3.hasError"
                      slot="helper"
                    >
                      {{ errors.businessAlias3.message }}
                    </span>
                  </InputText>
                  <InputText
                    ref="businessAlias4"
                    v-model.trim="businessAlias4"
                    label="Business Alias 4"
                    placeholder="Business Alias 4"
                    name="business-alias-4"
                    class="mt-5"
                    :has-error="errors.businessAlias4.hasError"
                    @blur="analytics.input.businessAlias('4')"
                  >
                    <span
                      v-if="errors.businessAlias4.hasError"
                      slot="helper"
                    >
                      {{ errors.businessAlias4.message }}
                    </span>
                  </InputText>
                </template>
              </template>
              <div class="business-alias-buttons">
                <button
                  v-if="flagBusinessAliasEnabled"
                  id="biz-alias-button"
                  ref="toggleBusinessAliases"
                  type="button"
                  class="text-blue-100 font-bold underline mt-5"
                  @click="showBusinessAlias = !showBusinessAlias; analytics.click.businessAliases(showBusinessAlias);"
                >
                  <template v-if="showBusinessAlias">
                    My business does not go by another name
                  </template>
                  <template v-else>
                    My business also goes by another name
                  </template>
                </button>
                <button
                  v-if="showBusinessAlias && enhancedBusinessAliasesEnabled"
                  id="biz-more-alias-button"
                  type="button"
                  class="biz-more-alias-button text-blue-100 font-bold underline mt-5"
                  @click.prevent="moreAlias()"
                >
                  <SvgPlusIcon
                    color="#3573b1"
                    :height="12"
                    :width="12"
                  /><span>Add more aliases</span>
                </button>
              </div>
            </template>
            <!-- Default non-business inputs -->
            <template v-if="moverType !== 'BUSINESS'">
              <InputText
                ref="firstName"
                v-model.trim="firstName"
                label="First Name"
                placeholder="First Name"
                name="first-name"
                class="mt-5"
                :has-error="errors.firstName.hasError"
                :disabled="ivsProofingCompleted"
                @input="errors.firstName.hasError = false"
                @blur="analytics.input.firstName"
              >
                <span
                  v-if="errors.firstName.hasError"
                  slot="helper"
                >
                  {{ errors.firstName.message }}
                </span>
              </InputText>
              <InputText
                v-model="middleName"
                label="Middle Name or Initial"
                placeholder="Middle Name or Initial"
                name="middle-initial"
                class="mt-5"
                :has-error="middleName ? errors.middleName.hasError : false"
                :disabled="ivsProofingCompleted"
                @input="middleName ? errors.middleName.hasError = false : null"
              >
                <span
                  v-if="middleName && errors.middleName.hasError"
                  slot="helper"
                >
                  {{ errors.middleName.message }}
                </span>
              </InputText>
              <InputText
                ref="lastName"
                v-model.trim="lastName"
                label="Last Name"
                placeholder="Last Name"
                name="last-name"
                class="mt-5"
                :has-error="errors.lastName.hasError"
                :disabled="ivsProofingCompleted"
                @blur="analytics.input.lastName"
                @input="errors.lastName.hasError = false"
              >
                <span
                  v-if="errors.lastName.hasError"
                  slot="helper"
                >
                  {{ errors.lastName.message }}
                </span>
              </InputText>
              <InputSuffix
                v-model="suffix"
                label="Suffix"
                name="suffix"
                class="mt-5"
                :disabled="ivsProofingCompleted"
              />
            </template>
            <InputText
              ref="email"
              v-model.trim="email"
              label="Email Address"
              placeholder="hello@mail.com"
              name="email"
              class="mt-5"
              :has-error="errors.email.hasError"
              :disabled="ivsProofingCompleted || !primaryBusiness"
              @blur="analytics.input.email"
              @input="errors.email.hasError = false"
            >
              <span
                v-if="errors.email.hasError"
                slot="helper"
              >
                {{ errors.email.message }}
              </span>
            </InputText>
            <InputText
              ref="emailConfirm"
              v-model.trim="emailConfirm"
              label="Confirm Email Address"
              placeholder="hello@mail.com"
              name="email-confirm"
              class="mt-5"
              :has-error="errors.emailConfirm.hasError"
              :disabled="ivsProofingCompleted || !primaryBusiness"
              @input="errors.emailConfirm.hasError = false"
              @blur="analytics.input.emailConfirm"
            >
              <span slot="helper">
                {{ errors.emailConfirm.message }}
              </span>
            </InputText>
            <div class="mt-5">
              <p class="text-sm">
                By checking the box below, you are opting-in to a one-time reminder email in the event you don't complete your Change-of-Address (COA) request. If checked, USPS will send one email with a link to resume your COA. Your COA won't take effect until the full form is complete.
              </p>
            </div>
            <div class="mt-5">
              <label class="reminder_email--label text-base">
                I opt-in, to receive a one-time reminder email
                <input
                  id="optin-mail-reminder"
                  :checked="optInMailReminder"
                  class="mt-3"
                  type="checkbox"
                  @click="optInMailReminder = !optInMailReminder; analytics.click.optInReminder(optInMailReminder)"
                >
                <span class="checkmark" />
              </label>
            </div>
            <div
              v-if="twoFactorAuthEnabled"
              class="mt-5"
            >
              <DpoPhoneNumberDisclaimer />
            </div>
            <div class="flex mt-5">
              <div class="w-1/2 mr-3 disclaimer-phone-number">
                <InputPhone
                  ref="phone"
                  v-model="phoneNumber"
                  :label="phoneNumLabel"
                  placeholder="(000) 000-0000"
                  name="phone"
                  :has-error="errors.phoneNumber.hasError"
                  :disabled="ivsProofingCompleted || !primaryBusiness"
                  @input="errors.phoneNumber.hasError = false"
                  @blur="analytics.input.phone"
                >
                  <span
                    v-if="errors.phoneNumber.hasError"
                    slot="helper"
                  >
                    {{ errors.phoneNumber.message }}
                  </span>
                </InputPhone>
              </div>
              <div class="w-1/2 ml-3 disclaimer-phone-type">
                <InputDropdown
                  v-if="!twoFactorAuthEnabled"
                  ref="phoneType"
                  v-model="phoneType"
                  label="Phone Type"
                  name="phone-type"
                  :disabled="!primaryBusiness"
                  :has-error="errors.phoneType.hasError"
                  @input="analytics.input.phoneType()"
                >
                  <option
                    value="MOBILE"
                  >
                    Mobile
                  </option>
                  <option
                    value="HOME"
                  >
                    Home
                  </option>
                  <span
                    v-if="errors.phoneType.hasError"
                    slot="helper"
                  >
                    {{ errors.phoneType.message }}
                  </span>
                </InputDropdown>
              </div>
            </div>
          </div>
          <div
            v-if="moverType === '' || moverType === 'INDIVIDUAL'"
            class="w-1/2 pl-xl"
          >
            <p><strong>First Name (or Initial)</strong></p>
            <p
              class="mb-5"
            >
              Enter your first name or initial.
            </p>
            <p><strong>Middle Name (or Initial)</strong></p>
            <p
              class="mb-5"
            >
              Enter your middle name or initial.
            </p>
            <p><strong>Last Name</strong></p>
            <p class="mb-5">
              Fill in only one last name. If anyone with the same
              last name is moving to a different address, use a
              separate Change-of-Address for each person.
            </p>
            <p ref="filler-note">
              Note: If you receive mail by more than one name
              (maiden name, married name or nickname) fill out a
              separate Change-of-Address for each name and choose Individual.
            </p>
          </div>
          <div
            v-if="moverType === 'FAMILY'"
            class="w-1/2 pl-xl"
          >
            <p ref="filler-fname-header">
              <strong>First Name (or Initial)</strong>
            </p>
            <p
              ref="filler-fname-text"
              class="mb-5"
            >
              Enter the first name or initial of the head of the household.
            </p>
            <p ref="filler-mname-header">
              <strong>Middle Name (or Initial)</strong>
            </p>
            <p
              ref="filler-mname-text"
              class="mb-5"
            >
              Enter the middle name or initial of the head of the household.
            </p>
            <p ref="filler-lname-header">
              <strong>Last Name</strong>
            </p>
            <p
              ref="filler-lname-text"
              class="mb-5"
            >
              Fill in only one last name. If anyone with the same last
              name is moving to a different address, use a separate
              Change-of-Address for each person.
            </p>
            <p ref="filler-note">
              Note: If you receive mail by more than one name
              (maiden name, married name or nickname) fill out a separate
              Change-of-Address for each name and choose Individual.
            </p>
          </div>
          <div
            v-if="moverType === 'BUSINESS'"
            class="w-1/2 pl-xl"
          >
            <div v-if="multipleAddresses !== 'YES'">
              <p><strong>Business Name</strong></p>
              <p class="mb-5">
                Enter the primary business name in this field.
                <template v-if="flagBusinessAliasEnabled">
                  If you receive mail by more than one business name, please
                  include each business alias name in the expandable fields.
                </template>
              </p>
              <p v-if="flagBusinessAliasEnabled">
                Each alias name added will be treated as its own Change-of-Address
                order. Only one email address will be collected and applied to all
                primary and alias names.
              </p>
            </div>
            <div v-else>
              <p>
                The email address and phone number of the "Primary" business are
                applied to all businesses listed under saved businesses.
              </p>
            </div>
          </div>
        </section>
        <div class="c-hairline" />
        <section class="flex py-8">
          <div class="w-1/2 max:pr-xl">
            <h3
              id="forward-type"
              class="font-bold mb-5"
            >
              Are you planning on returning to your old
              address in six months or less?
            </h3>
            <fieldset
              class="flex flex-no-wrap"
              aria-labelledby="forward-type"
            >
              <InputRadio
                ref="temporary"
                v-model="forwardType"
                form-value="TEMPORARY"
                name="forward-type"
                button
                class="mr-6"
                :disabled="!primaryBusiness"
                @input="forwardType = $event; analytics.click.forwardType('TEMPORARY');"
              >
                Yes
              </InputRadio>
              <InputRadio
                ref="permanent"
                v-model="forwardType"
                form-value="PERMANENT"
                name="forward-type"
                button
                class="mr-6"
                :disabled="!primaryBusiness"
                @input="forwardType = $event; analytics.click.forwardType('PERMANENT');"
              >
                No
              </InputRadio>
            </fieldset>
            <p
              v-if="errors.forwardType.hasError"
              class="errors text-red-200 text-sm font-bold"
            >
              {{ errors.forwardType.message }}
            </p>
          </div>
          <div class="w-1/2 pl-xl">
            <p ref="filler-type-temp">
              Selecting "Yes" will classify your Change-of-Address as Temporary.
            </p>
            <p
              ref="filler-type-perm"
              class="mt-5"
            >
              Selecting "No" will classify your Change-of-Address as Permanent.
            </p>
            <p
              v-if="multipleAddresses === 'YES'"
              class="mt-5"
            >
              The forwarding type applies to all businesses listed under saved
              businesses. You will have an opportunity to change the forwarding
              type for each business on the verify-info page.
            </p>
          </div>
        </section>
        <div class="c-hairline" />
        <section class="flex py-8">
          <div class="w-1/2 max:pr-xl">
            <h3
              class="font-bold mb-5"
            >
              When should we start forwarding mail to your new address?
            </h3>
            <InputDatePicker
              ref="startDate"
              v-model="startDate"
              :label="forwardType !== 'TEMPORARY' ? 'Mail Forwarding Date' : 'Start Forwarding'"
              :min="minStartDate"
              :max="maxStartDate"
              desktop
              name="forward-start-date"
              class="mt-3"
              :has-error="errors.startDate.hasError"
              :disabled="!primaryBusiness"
              @input="analytics.input.startDate()"
            >
              <span slot="helper">
                {{ errors.startDate.message }}
              </span>
            </InputDatePicker>
            <InputDatePicker
              v-if="forwardType === 'TEMPORARY'"
              ref="stopDate"
              v-model="stopDate"
              :min="minStopDate"
              :max="maxStopDate"
              :open-date="minStopDate"
              desktop
              label="End Forwarding"
              name="forward-stop-date"
              class="mt-3"
              :has-error="errors.stopDate.hasError"
              :disabled="!primaryBusiness"
              @input="analytics.input.stopDate()"
            >
              <span slot="helper">
                {{ errors.stopDate.message }}
              </span>
            </InputDatePicker>
          </div>
          <div class="w-1/2 pl-xl">
            <p>
              Enter the date you'd like us to begin forwarding your mail to your
              new address. The date you enter must not be more than 30 days prior
              to today, or more than 3 months from today. Mail should arrive at
              your new address 7-10 postal business days after your move effective
              date OR your file date, whichever date is later.
            </p>
            <template v-if="forwardType !== 'PERMANENT'">
              <p class="mt-4">
                The minimum length for filing a temporary Change-of-Address is
                15 days. The initial forwarding period is restricted to 6 months,
                but can be extended up to 1 year.
              </p>
            </template>
            <p
              v-if="multipleAddresses === 'YES'"
              class="mt-4"
            >
              The forwarding start <span v-if="forwardType !== 'PERMANENT'">and end</span>
              date applies to businesses listed under saved businesses. You will have the
              opportunity to change the forwarding start <span v-if="forwardType !== 'PERMANENT'">and end</span>
              date for each business on the verify-info page.
            </p>
          </div>
        </section>
        <div class="c-hairline" />
        <template v-if="multipleAddresses === 'YES'">
          <MultipleBusinessAddresses
            ref="multipleAddressesComponent"
            :forward-type-val="forwardType"
            :is-simplify="simplify"
            :start-date-val="startDate"
            :stop-date-val="stopDate"
            :business-whole-company-val="businessWholeCompany"
            :email-val="email"
            :phone-number-val="phoneNumber"
            :phone-type-val="phoneType"
            view-mode="choice"
            this.errors
            @primary="setPrimaryBusiness"
            @validateForm="validateForm()"
            @fieldErrors="(fieldErrors)=>updateFieldErrors(fieldErrors)"
            @fieldErrorsMilitary="updateFieldErrorsMilitary"
            @disableNonBusinessMovers="disableMoverType"
            @updateForwardFields="updateForwardingFields"
          />
        </template>
        <div v-else>
          <section
            class="flex py-8 c-hairline-after"
          >
            <div class="w-1/2 max:pr-xl">
              <h3 class="font-bold">
                What's your old address?
              </h3>
              <InputZipCode
                ref="oldZipCode"
                v-model="oldZipCode"
                label="ZIP Code™"
                placeholder="00000"
                name="old-zip-code"
                class="mt-5"
                :choice-experience="true"
                :has-error="errors.oldZipCode.hasError"
                :async-fn="zipSearch({
                  cityName: oldCity,
                  state: oldState,
                })"
                :transform-compare="zipCompare"
                :transform-label="zipLabel"
                :transform-value="zipValue"
                :disabled="ivsProofingCompleted"
                @blur="analytics.input.oldZipCode"
                @select="oldCity = $event.cityName;
                         oldState = $event.state"
              >
                <template slot="helper">
                  {{ errors.oldZipCode.message }}
                </template>
              </InputZipCode>
              <div class="choice__selections--unstack">
                <InputListBox
                  ref="oldCity"
                  v-model="oldCity"
                  label="City"
                  placeholder="City"
                  name="old-city"
                  class="mt-5"
                  :has-error="errors.oldCity.hasError"
                  :async-fn="citySearch({
                    zip: oldZipCode,
                    state: oldState,
                  })"
                  :transform-compare="zipCompare"
                  :transform-label="cityLabel"
                  :transform-value="cityValue"
                  :disabled="ivsProofingCompleted"
                  @blur="analytics.input.oldCity"
                  @select="oldZipCode = $event.zip; oldState = $event.state"
                >
                  {{ errors.oldCity.message }}
                </InputListBox>
                <InputState
                  ref="oldState"
                  v-model="oldState"
                  label="State"
                  name="old-state"
                  class="mt-5"
                  :has-error="errors.oldState.hasError"
                  :disabled="ivsProofingCompleted"
                  @input="analytics.input.oldState()"
                >
                  <span
                    v-if="errors.oldState.hasError"
                    slot="helper"
                  >
                    {{ errors.oldState.message }}
                  </span>
                </InputState>
              </div>
              <InputListBox
                ref="oldStreet"
                v-model="oldStreetAddress"
                label="Street Address"
                name="old-street-address"
                placeholder="Street Address"
                class="mt-5"
                :has-error="errors.oldStreetAddress.hasError && !showOldPoBox"
                :async-fn="streetSearch({
                  cityName: oldCity,
                  state: oldState,
                  zipcode: oldZipCode,
                })"
                :transform-compare="streetCompare"
                :transform-label="streetLabel"
                :transform-value="streetValue"
                :disabled="ivsProofingCompleted"
                @input="oldPoBoxNumber = ''"
                @blur="analytics.input.oldStreet"
                @select="oldZipCode=$event.zip;
                         oldCity=$event.cityName;
                         oldState=$event.state;"
              >
                {{ errors.oldStreetAddress.message }}
              </InputListBox>
              <InputText
                v-if="oldState === 'PR'"
                ref="oldUrbanizationName"
                v-model="oldUrbanizationName"
                label="Urbanization Name (Puerto Rico only)"
                name="old-urbanization-name"
                placeholder="URB HIGHLAND GDNS"
                class="mt-5"
                :has-error="errors.oldUrbanizationName.hasError"
                :disabled="ivsProofingCompleted"
                @blur="analytics.input.oldUrbanizationName"
              >
                <span slot="helper">
                  {{ errors.oldUrbanizationName.message }}
                </span>
              </InputText>
              <InputText
                v-if="showOldPoBox"
                ref="oldPoBox"
                v-model="oldPoBoxNumber"
                label="Po Box"
                name="old-po-box"
                placeholder="PO Box 123"
                class="mt-5"
                :has-error="errors.oldPoBoxNumber.hasError"
                :disabled="ivsProofingCompleted"
                @blur="analytics.input.oldPoBox"
                @input="oldAddressError = ''"
              >
                <span slot="helper">
                  {{ errors.oldPoBoxNumber.message }}
                </span>
              </InputText>
            </div>
            <div class="w-1/2 pl-xl">
              <p>
                This is the address you're moving from. Enter a valid address,
                and remember to include apartment/suite number if applicable.
              </p>
              <p
                class="mt-5"
              >
                If your address is a PO Box, enter it in the Street Address
                field for example, PO Box 1001. Please note that Change-of-Address
                requests from non-USPS Private Mail Boxes (PMB) cannot be processed.
              </p>
              <p
                v-if="oldPoBoxException"
                class="errors text-red-200 mt-5"
              >
                <span class="font-bold">Note:</span> The address you provided is not
                considered a proper mailing address within this ZIP Code™. A proper
                mailing address within this ZIP Code™ requires a PO Box address.
                Please input your PO Box number in the “PO Box” field and click "Next"
                to continue.<br><span class="font-bold">A PO Box is required to proceed.</span>
              </p>
            </div>
          </section>
          <div class="c-hairline" />
          <section
            class="flex py-8"
          >
            <div class="w-1/2 max:pr-xl">
              <h3 class="font-bold">
                What's your new address?
              </h3>
              <InputZipCode
                ref="newZipCode"
                v-model="newZipCode"
                label="ZIP Code™"
                placeholder="00000"
                name="new-zip-code"
                class="mt-5"
                :choice-experience="true"
                :has-error="errors.newZipCode.hasError"
                :async-fn="zipSearch({
                  cityName: newCity,
                  state: newState,
                })"
                :transform-compare="zipCompare"
                :transform-label="zipLabel"
                :transform-value="zipValue"
                @blur="analytics.input.newZipCode"
                @select="newCity = $event.cityName;
                         newState = $event.state"
              >
                <template slot="helper">
                  {{ errors.newZipCode.message }}
                </template>
              </InputZipCode>
              <div class="choice__selections--unstack">
                <InputListBox
                  ref="newCity"
                  v-model="newCity"
                  label="City"
                  placeholder="City"
                  name="new-city"
                  class="mt-5"
                  :has-error="errors.newCity.hasError"
                  :async-fn="citySearch({
                    zip: newZipCode,
                    state: newState,
                  })"
                  :transform-compare="zipCompare"
                  :transform-label="cityLabel"
                  :transform-value="cityValue"
                  @blur="analytics.input.newCity"
                  @select="newZipCode = $event.zip; newState = $event.state"
                >
                  {{ errors.newCity.message }}
                </InputListBox>
                <InputState
                  ref="newState"
                  v-model="newState"
                  label="State"
                  name="new-state"
                  class="mt-5"
                  :has-error="errors.newState.hasError"
                  @input="analytics.input.newState()"
                >
                  <span
                    v-if="errors.newState.hasError"
                    slot="helper"
                  >
                    {{ errors.newState.message }}
                  </span>
                </InputState>
              </div>
              <InputListBox
                ref="newStreet"
                v-model="newStreetAddress"
                label="Street Address"
                name="new-street-address"
                placeholder="Street Address"
                class="mt-5"
                :has-error="errors.newStreetAddress.hasError && !showNewPoBox && !showNewPrivateMailbox"
                :async-fn="streetSearch({
                  cityName: newCity,
                  state: newState,
                  zipcode: newZipCode,
                })"
                :transform-compare="streetCompare"
                :transform-label="streetLabel"
                :transform-value="streetValue"
                @input="newPoBoxNumber = ''; newPrivateMailbox = '';"
                @blur="analytics.input.newStreet"
                @select="newZipCode=$event.zip;
                         newCity=$event.cityName;
                         newState=$event.state;"
              >
                {{ errors.newStreetAddress.message }}
              </InputListBox>
              <InputText
                v-if="newState === 'PR'"
                ref="newUrbanizationName"
                v-model="newUrbanizationName"
                label="Urbanization Name (Puerto Rico only)"
                name="new-urbanization-name"
                placeholder="URB HIGHLAND GDNS"
                class="mt-5"
                :has-error="errors.newUrbanizationName.hasError"
                @blur="analytics.input.newUrbanizationName"
              >
                <span slot="helper">
                  {{ errors.newUrbanizationName.message }}
                </span>
              </InputText>
              <InputText
                v-if="showNewPoBox"
                ref="newPoBox"
                v-model="newPoBoxNumber"
                label="Po Box"
                name="new-po-box"
                placeholder="PO Box 123"
                class="mt-5"
                :has-error="errors.newPoBoxNumber.hasError"
                @blur="analytics.input.newPoBox"
              >
                <template slot="helper">
                  {{ errors.newPoBoxNumber.message }}
                </template>
              </InputText>
              <InputText
                v-if="showNewPrivateMailbox"
                ref="newPrivateMailbox"
                v-model="newPrivateMailbox"
                label="Private Mailbox"
                name="new-private-mailbox"
                placeholder="PMB 123"
                class="mt-5"
                :has-error="errors.newPrivateMailbox.hasError"
                @blur="analytics.input.newPrivateMailbox"
              >
                <template slot="helper">
                  {{ errors.newPrivateMailbox.message }}
                </template>
              </InputText>
            </div>
            <div class="w-1/2 pl-xl">
              <p>
                This is the address you're moving to. Enter a valid address,
                and remember to include apartment/suite number if applicable.
              </p>
              <p
                class="mt-5"
              >
                If your address is a PO Box, enter it in the Street
                Address field for example, PO Box 1001. Please note
                that Change-of-Address request from non-USPS Private
                Mail Boxes (PMB) cannot be processed.
              </p>
              <p
                v-if="newPoBoxException"
                class="errors text-red-200 mt-5"
              >
                <span class="font-bold">Note:</span> The address you
                provided is not considered a proper mailing address
                within this ZIP Code™. A proper mailing address within
                this ZIP Code™ requires a PO Box address. Please input
                your PO Box number in the “PO Box” field and click "Next"
                to continue.<br><span class="font-bold">A PO Box is required to proceed.</span>
              </p>
            </div>
          </section>
        </div>
        <div class="c-hairline" />
        <section class="py-8">
          <div class="w-2/3">
            <p class="font-bold text-base">
              Request for Change-of-Address and Forwarding Services
            </p>
            <p
              class="mt-2 mb-5 text-base"
            >
              <strong>Privacy Act Statement: </strong> Your information will be used to provide mail forwarding and
              Change-of-Address (COA) services and move related advertisements, to verify your identity, to facilitate
              communication, to facilitate surveys regarding the COA process, to identify and mitigate potential fraud,
              and to provide address correction services. Collection is authorized by 39 U.S.C. 401, 403, 404.
              Providing the information is voluntary, but if not supplied, we may not be able to process and fulfill
              your request for services. We do not disclose your information to third parties without your consent,
              except to facilitate the transaction, to act on your behalf or request, or as legally required.
              <button
                v-if="!privacyActFullContent"
                class="font-bold block mt-2"
                :class="['c-link--choice', 'c-link--inline', 'text-left']"
                type="button"
                @click="togglePrivacyActContent"
              >
                Click here to display the entire Privacy Act Statement
              </button>
              <template v-if="privacyActFullContent">
                This includes the following standard
                and specific routine uses: Incident to legal proceedings; for law enforcement
                purposes; to a congressional office; to agents or contractors; to auditors;
                to labor organizations; to federal, state, local, or foreign government
                agencies; to agencies and entities for financial matters; for customer service
                purposes; to a federal, state, or local government agency upon certification
                when required for the performance of its duties; to a law enforcement agency
                after the Postal Inspection Service has confirmed that the COA information is
                needed for a criminal investigation; for service of process; for purpose of
                jury service; to agencies or entities at customer’s request; to a disaster
                relief organization; pursuant to the order of a court of competent jurisdiction;
                for address correction services only if the mailer is in possession of the name
                and old address; and to USPS licensed service providers to perform mailing list
                address correction services for lists containing your name and old address. For
                a list of licensed service providers please visit <a
                  href="https://postalpro.usps.com/NCOALink_Service_Providers"
                  :class="['c-link--choice', 'c-link--inline']"
                >https://postalpro.usps.com/NCOALink_Service_Providers</a>. For more
                information regarding our privacy policies visit <a
                  href="https://www.usps.com/privacypolicy"
                  target="_blank"
                  :class="['c-link--choice', 'c-link--inline']"
                  rel="noopener noreferrer"
                >www.usps.com/privacypolicy</a>.
                <button
                  :class="['c-link--choice', 'c-link--inline']"
                  type="button"
                  @click="togglePrivacyActContent"
                >
                  Display Less
                </button>
              </template>
            </p>
            <p
              v-if="twoFactorAuthEnabled"
              class="font-bold text-base"
            >
              Identity Verification and Online Identity Proofing
            </p>
            <p
              v-if="twoFactorAuthEnabled"
              class="mt-2 mb-9 text-base"
            >
              <strong>Privacy Act Statement:</strong> Your information will be used to verify
              your identity using enhanced remote identity proofing when applying for
              Change-of-Address (COA) services online, to improve the security of the COA
              process, and to identify and mitigate potential fraud. Collection is authorized
              by 39 U.S.C. 401, 403, 404, and 411. Providing your information is voluntary,
              but if not provided, we may not be able to verify your identity online when
              applying for COA and mail forwarding services.
              <button
                v-if="!identityProofingFullContent"
                class="font-bold block mt-2"
                :class="['c-link--choice', 'c-link--inline', 'text-left']"
                type="button"
                @click="toggleIdentityProofingContent"
              >
                Click here to display the entire Privacy Act Statement
              </button>
              <template v-if="identityProofingFullContent">
                We do not disclose your information without your consent to third parties,
                except for the following limited circumstances: incident to legal proceedings
                involving the Postal Service; for law enforcement purposes; to a congressional
                office on your behalf; to agents or contractors when necessary to fulfill a
                business function; to a U.S. Postal Service auditor; to labor organizations as
                required by applicable law; to government agencies in connection with decisions
                as necessary; to agencies and entities for financial matters; and for customer
                service purposes. For more information on our privacy policies visit <a
                  href="https://www.usps.com/privacypolicy"
                  target="_blank"
                  :class="['c-link--choice', 'c-link--inline']"
                  rel="noopener noreferrer"
                >www.usps.com/privacypolicy</a>.
                <button
                  :class="['c-link--choice', 'c-link--inline']"
                  type="button"
                  @click="toggleIdentityProofingContent"
                >
                  Display Less
                </button>
              </template>
            </p>
            <transition
              name="fade"
              appear
            >
              <section v-if="renderV3Challenge && !showOutageBanner">
                <p class="mb-4">
                  For your security, clicking the box below confirms that this
                  Change-of-Address form is being completed by a person, and not
                  an automated query.
                </p>
                <vue-recaptcha
                  id="recaptcha-challenge"
                  ref="recaptchaChallenge"
                  :sitekey="getRecaptchaV2Key()"
                  @click.native="analytics.click.recaptchaVerified"
                  @verify="recaptchaChallengeToken=$event;"
                  @expired="recaptchaChallengeToken='';"
                />
                <transition
                  appear
                  name="fade"
                >
                  <p
                    v-if="errors.recaptchaChallengeToken && errors.recaptchaChallengeToken.hasError"
                    class="c-input-text__helper c-input-helper errors"
                  >
                    {{ errors.recaptchaChallengeToken.message }}
                  </p>
                </transition>
              </section>
            </transition>
            <button
              v-if="!showOutageBanner"
              id="next"
              ref="nextBtn"
              :class="{ 'mt-5': renderV3Challenge }"
              class="c-btn"
              @click="analytics.click.next(); trackFirstEvent('next-btn');"
            >
              Next
            </button>
          </div>
        </section>
        <div class="c-hairline" />
      </form>
      <div class="flex flex-col mt-4 u-container--x">
        <Accordion summary="Payment Methods">
          <p>
            For your security, one step we take to verify your identity uses a valid credit or debit card.
            You will be charged {{ icoaAmountFormatted }} for this service.
          </p>
        </Accordion>
        <Accordion
          summary="Preparer Information"
          class="mt-6"
        >
          <p>
            <span class="font-bold block">Individual and Family Change-of-Address</span>
            By preparing this form I represent that I am the person for whom mail will be forwarded under this order
            and/or I am the legally authorized agent for the individual(s) in a household for whom mail will be forwarded.
            Examples of legally authorized agents for this purpose may include, but are not limited to, authority under
            a Power of Attorney, under a legal guardianship, or as the executor of the estate of the person. Anyone
            submitting false or inaccurate information on this form is subject to punishment by fine or imprisonment
            or both under Sections 2, 1001, 1702, and 1708 of Title 18, United States Code.
          </p>
          <p>
            <span class="font-bold block mt-3">Business Change-of-Address</span>
            By preparing this form, I represent that I am authorized to do so as the sole owner of the business for
            whom mail will be forwarded under this order, or I am the agent authorized to complete this form for
            the entity or partnership, for whom mail will be forwarded under this order. Anyone submitting false
            or inaccurate information on this form is subject to punishment by fine or imprisonment or both under
            Sections 2, 1001, 1702, and 1708 of Title 18, United States Code.
          </p>
        </Accordion>
        <div class="flex mt-8">
          <a
            href="https://about.usps.com/who/legal/privacy-policy/full-privacy-policy.htm"
            :class="['c-link--choice', 'c-link--inline']"
            target="_blank"
            rel="noopener noreferrer"
          >Privacy Policy</a>
          <router-link
            :to="{ name: 'terms-of-use' }"
            aria-label="Terms of Use for Change-of-Address"
            target="_blank"
            class="ml-4"
            :class="['c-link--choice', 'c-link--inline']"
          >
            <span>Terms of Use</span>
          </router-link>
        </div>
      </div>
      <div class="w-screen mt-8 bg-blue-200">
        <div class="flex items-center u-container--x disclaimer-banner-container">
          <div class="text-xl text-white mr-2">
            <a
              id="view-edit-coac"
              :href="this.$sanitize($options.coacUrl)"
              title="Official USPS Change-of-Address Correct Form"
              class="font-bold text-white text-base"
            >
              Manage your Change-of-Address on managemymove.com
            </a>
          </div>
          <a
            :href="this.$sanitize($options.coacUrl)"
            title="Official USPS Change-of-Address Correct Form"
            class="not-sr-only"
          >
            <span class="white-arrow" />
          </a>
        </div>
      </div>
    </main>
    <Modal
      ref="newConstructionModal"
      v-model="newConstructionModal"
      close-id="new-constr-close-x"
    >
      <ModalNewConstruction
        :street-address="newStreetAddress"
        :city="newCity"
        :state="newState"
        :zip-code="newZipCode"
        :urbanization-name="newUrbanizationName"
        @reset="newConstructionModal = false"
        @override="newConstructionOverride"
      />
    </Modal>
    <Modal
      ref="familyModal"
      v-model="moverTypeFamilyModal"
    >
      <h4>Based on your responses...</h4>
      <p
        class="mt-3"
      >
        {{
          familySameLastName === 'YES' || familyRemaining === 'YES'
            ? `A Family move will forward all mail associated with this last name.
            Based on your response, an Individual Change-of-Address must be done for
            each person moving to ensure the mail of the person remaining is unaffected
            by this Change-of-Address.`
            : ''
        }}
        {{
          familySameLastName === 'NO' && familyRemaining !== 'YES'
            ? `A Family move is only required when more than one person,
            with the same last name, is moving from the address. Anyone
            with a different last name must file an Individual Change-of-Address.`
            : ''
        }}
      </p>
      <button
        v-if="familySameLastName === 'YES' || familyRemaining === 'YES'"
        class="c-btn mt-3"
        @click="moverType='INDIVIDUAL'; moverTypeFamilyModal=false;"
      >
        Continue
      </button>
      <button
        v-if="familySameLastName === 'NO' && familyRemaining !== 'YES'"
        class="c-btn mt-3"
        @click="moverTypeFamilyModal=false;"
      >
        Continue
      </button>
    </Modal>
    <Modal
      ref="businessModal"
      v-model="moverTypeBusinessModal"
    >
      <h4>Based on your responses...</h4>
      <p
        class="mt-3"
      >
        A Business move should only be submitted when the entire business
        is moving. If the entire business is moving to a new address, please
        select Continue, and change your response to Yes.
      </p>
      <button
        class="c-btn mt-3"
        @click="moverType='INDIVIDUAL'; moverTypeBusinessModal=false;"
      >
        Continue
      </button>
    </Modal>
    <Modal
      ref="emailReminderOptOutModal"
      v-model="emailReminderOptOutModal"
    >
      <h4>One-Time Email Opt-Out</h4>
      <p class="mt-3">
        You've opted-out of the one-time reminder email if you don't
        complete this Change-of-Address transaction.
      </p>
      <p class="mt-3">
        You will still receive confirmation emails if you complete your
        transaction.
      </p>
      <button
        class="c-btn mt-3"
        @click="emailReminderOptOutModal=false"
      >
        Close
      </button>
    </Modal>
    <Modal
      ref="coaCheckModal"
      v-model="coaCheckModal"
    >
      <h4 class="font-bold">
        Based on your response...
      </h4>
      <p class="mt-3">
        A Change-of-Address already exists matching the name and address
        you've entered. To make changes to your current Change-of-Address,
        visit
        <a
          :class="['c-link--choice', 'c-link--inline']"
          :href="this.$sanitize($options.coacUrl)"
          rel="noopener noreferrer"
        >
          {{ $options.coacUrl }}
        </a>, enter your new ZIP Code™ and 16-digit confirmation code,
        which can be located within your confirmation email and the
        Confirmation Notification Letter you received at your new address.
      </p>
    </Modal>
  </div>
</template>

<script>
/* eslint-disable max-len */
import VueRecaptcha from 'vue-recaptcha';
// MGO UI Components
import {
  Accordion,
  Banner,
  InputDatePicker,
  InputDropdown,
  InputListBox,
  InputPhone,
  InputRadio,
  InputState,
  InputSuffix,
  InputText,
  InputZipCode,
  Modal,
} from '@mgo/ui/lib/components';
import Cookies from 'js-cookie';
import { identify, track } from '../../analytics/events';

// mixin used in both Disclaimer/desktop.vue as well as MoveCombined/mobile.vue
import { desktopAnalytics } from '../MoveCombined/analytics';
import moveCombinedMixin from '../MoveCombined/mixin';
import MultipleBusinessAddresses from '../../components/MultipleAddresses/desktop.vue';
import addressViolations from '../../mixins/addressViolations';
import datePicker from '../../mixins/datePicker';
import destinationManager from '../../mixins/destinationManager';
import emailReminder from '../../mixins/emailReminder';
import grecaptcha from '../../mixins/grecaptcha';
import moverType from '../../mixins/moverType';
import newConstruction from '../../mixins/newConstruction';
import rapidKey from '../../mixins/rapidKey';
import ModalNewConstruction from '../../components/ModalNewConstruction.vue';

// SVG Vue Components
import SvgBusiness from '../../components/SvgBusiness.vue';
import SvgFamily from '../../components/SvgFamily.vue';
import SvgIndividual from '../../components/SvgIndividual.vue';

import IndividualProgressBar from '../../../../ui/lib/components/progress-bar/IndividualProgressBar.vue';

import BusinessAlias from '../../components/BusinessAlias.vue';
import DpoPhoneNumberDisclaimer from '../../components/DpoPhoneNumberDisclaimer.vue';
import SvgPlusIcon from '../../components/SvgPlusIcon.vue';

export default {
  name: 'DisclaimerChoice',
  components: {
    Accordion,
    Banner,
    IndividualProgressBar,
    InputDatePicker,
    InputDropdown,
    InputListBox,
    InputPhone,
    InputRadio,
    InputState,
    InputSuffix,
    InputText,
    InputZipCode,
    Modal,
    ModalNewConstruction,
    MultipleBusinessAddresses,
    SvgBusiness,
    SvgFamily,
    SvgIndividual,
    VueRecaptcha,
    BusinessAlias,
    DpoPhoneNumberDisclaimer,
    SvgPlusIcon,
  },
  mixins: [
    addressViolations,
    datePicker,
    destinationManager,
    emailReminder,
    grecaptcha,
    moveCombinedMixin,
    moverType,
    newConstruction,
    rapidKey,
  ],
  data() {
    return {
      identityProofingFullContent: false,
      privacyActFullContent: false,
      current: 1,
      emailConfirm: '',
      optInMailReminder: false,
    };
  },
  computed: {
    totalSteps() {
      return this.informedDeliveryEligible ? 7 : 6;
    },
    progressStep() {
      return this.current / this.totalSteps;
    },
    isFamily() {
      return true;
    },
    analytics() {
      return desktopAnalytics();
    },
    flagAmpersendEnabled() {
      return this.$store.get('flagAmpersendEnabled');
    },
    showOutageBanner() {
      return this.$store.get('flagOutageBanner');
    },
    showUpcomingMaintenanceBanner() {
      return this.$store.get('upcomingMaintenance');
    },
  },
  async mounted() {
    this.offersChoice = JSON.parse(sessionStorage.getItem('offersChoice') ?? false);
    this.autofillChoice = JSON.parse(sessionStorage.getItem('autofillChoice') ?? false);
    this.informedDelivery = JSON.parse(sessionStorage.getItem('informedDelivery'));
    this.optInMailReminder = JSON.parse(sessionStorage.getItem('optInMailReminder') ?? false);
    if (Cookies.get('coa_complete')) {
      this.$deleteSession();
      this.$coa.reset();
      Cookies.remove('coa');
      Cookies.remove('coa_complete');
    }
    if (this.$coa.coaUUID) {
      sessionStorage.setItem('coaUUID', this.$coa.coaUUID);
      await this.hydrateCOA();
      await Promise.all([
        this.hydrateMover(),
        this.hydrateAddresses(),
      ]);
    } else {
      const utmMedium = this.$route.query.utm_medium ?? '';
      await this.$coa.coaCreate(utmMedium);
      this.$setSession(this.$coa.coaUUID, this.$coa.orderUuid);
      if (this.flagAmpersendEnabled) {
        this.$amp.setAnonymousID(this.$coa.coaUUID);
        await this.$amp.identify({
          isVerified: false,
          mgoDeviceType: 'DESKTOP',
        });
      }
      identify(this.$coa.coaUUID);
      track('CoaCreated', {
        uuid: this.$coa.coaUUID,
        visitId: this.$coa.visitId,
      });
    }

    if (this.$store.get('config@twoFactorAuthEnabled') && this.$store.get('mfa@allowProfiling')) {
      this.deviceProfiling();
    }

    this.pageView();
    this.dirty = false;
  },
  methods: {
    togglePrivacyActContent() {
      this.privacyActFullContent = !this.privacyActFullContent;
    },
    toggleIdentityProofingContent() {
      this.identityProofingFullContent = !this.identityProofingFullContent;
    },
    showBusinessAliasHandle() {
      this.showBusinessAlias = !this.showBusinessAlias;
    },
    async submit() {
      this.improvedFieldErrors = {};
      sessionStorage.setItem('offersChoice', this.offersChoice);
      sessionStorage.setItem('autofillChoice', this.autofillChoice);
      sessionStorage.setItem('informedDeliveryEligible', this.informedDeliveryEligible);
      sessionStorage.setItem('startDate', this.startDate);
      sessionStorage.setItem('temp', this.forwardType === 'TEMPORARY');
      sessionStorage.setItem('optInMailReminder', this.optInMailReminder);


      if (this.showOutageBanner) return;
      this.$store.commit('ENABLE_LOADER');

      if (!this.informedDeliveryEligible) {
        this.informedDelivery = false;
      }

      if (this.twoFactorAuthEnabled) {
        this.phoneType = 'MOBILE';
      }

      const { medUpdated, sdUpdated, xhr } = await this.$coa.coaUpdate({
        moverType: this.moverType,
        forwardType: this.forwardType,
        startDate: this.startDate,
        informedDelivery: this.informedDelivery,
        stopDate: this.forwardType === 'TEMPORARY' ? this.stopDate : null,
        businessName: this.moverType === 'BUSINESS' ? this.businessName : null,
        email: this.email || null,
        famPrompt1ShareLastName: this.familySameLastName
          ? this.familySameLastName === 'YES'
          : null,
        famPrompt2SameLastNameRemaining: this.familyRemaining
          ? this.familyRemaining === 'YES'
          : null,
        bizPromptEntireMoving: this.businessWholeCompany
          ? this.businessWholeCompany === 'YES'
          : null,
        multipleAddresses: this.multipleAddresses === 'YES' ?? false,
      });

      this.attachFieldErrors({ xhr });

      // start/stop date updated due to email domain check
      this.medUpdated = medUpdated;
      this.sdUpdated = sdUpdated;

      if (xhr?.exception || this.fieldErrors?.length) {
        this.$store.commit('DISABLE_LOADER');
        await this.$nextTick();
        this.scrollToFirstError();
        return;
      }

      track('CoaUpdated',
        {
          dcoa: this.dcoa,
          forwardType: this.forwardType,
          informedDelivery: this.informedDelivery,
          moverType: this.moverType,
          startDate: this.startDate,
          stopDate: this.stopDate,
        });


      let businessAliasPayload = [];
      if (this.showBusinessAlias) {
        businessAliasPayload = this.enhancedBusinessAliasesEnabled
          ? this.businessAliases.map((d) => d.value).filter((v) => v)
          : [
            this.businessAlias1,
            this.businessAlias2,
            this.businessAlias3,
            this.businessAlias4,
          ].filter((v) => v);
      }
      const businessAliasHandle = this.flagBusinessAliasEnabled
        && this.moverType === 'BUSINESS' && this.$coa.businessAliasSave(businessAliasPayload);

      const addressResponses = await this.$coa.addressSaveV2({
        addressType: 'OLD',
        streetAddress: this.oldStreetAddress,
        city: this.oldCity,
        state: this.oldState,
        zipCode: this.oldZipCode,
        urbanizationName: this.oldState === 'PR'
          ? this.oldUrbanizationName
          : null,
        privateMailbox: null,
        poBoxNumber: this.oldPoBoxNumber || null,
        recentConstruction: false,
        overrideAms: false,
        moverType: this.moverType,
        forwardType: this.forwardType,
      }, {
        addressType: 'NEW',
        streetAddress: this.newStreetAddress,
        city: this.newCity,
        state: this.newState,
        zipCode: this.newZipCode,
        urbanizationName: this.newState === 'PR'
          ? this.newUrbanizationName
          : null,
        privateMailbox: this.newPrivateMailbox || null,
        poBoxNumber: this.newPoBoxNumber || null,
        recentConstruction: this.recentConstruction,
        overrideAms: this.overrideAms,
        moverType: this.moverType,
        forwardType: this.forwardType,
      });

      const personHandle = this.$coa.personSave({
        personType: 'MOVER',
        firstName: this.moverType !== 'BUSINESS' ? this.firstName : null,
        middleName: this.moverType !== 'BUSINESS' ? this.middleName : null,
        lastName: this.moverType !== 'BUSINESS' ? this.lastName : null,
        suffix: this.moverType !== 'BUSINESS' ? this.suffix : null,
        email: this.email,
        emailConfirm: this.email,
        [`${this.phoneType.toLowerCase()}Phone`]: this.phoneNumber,
      });

      const [
        personResponse,
        businessAliasResponse,
      ] = await Promise.all([
        personHandle,
        businessAliasHandle,
      ]);

      if (this.optInMailReminder) {
        this.$coa.emailReminderOptIn();
      } else {
        this.$coa.emailReminderOptOut();
      }

      this.attachFieldErrors(personResponse);
      this.attachFieldErrors(businessAliasResponse);
      this.prepareFieldErrors(personResponse);

      track(this.moverUUID ? 'PersonUpdated' : 'PersonCreated',
        {
          personType: 'MOVER',
          phoneType: this.phoneType,
        });

      if (addressResponses?.xhr?.exception) {
        if (addressResponses?.xhr?.exception === 'MilitaryZip5ForwardTypeException') {
          this.fieldErrors = [
            ...this.fieldErrors,
            { field: 'forwardType', message: addressResponses?.xhr?.message || '' },
          ];
          this.errors.forwardType.message = addressResponses?.xhr?.message || '';
          this.errors.forwardType.hasError = true;
        } else if (addressResponses?.xhr?.exception === 'MilitaryZip5MoverTypeException') {
          this.fieldErrors = [
            ...this.fieldErrors,
            { field: 'moverType', message: addressResponses?.xhr?.message || '' },
          ];
          this.errors.moverType.message = addressResponses?.xhr?.message || '';
          this.errors.moverType.hasError = true;
        } else if (addressResponses?.xhr?.addressType === 'NEW') {
          this.newAddressError = addressResponses?.xhr?.exception || '';
          this.errors.newStreetAddress.hasError = true;
          this.attachFieldErrors(this.newAddressError, 'new');
        } else {
          this.oldAddressError = addressResponses?.xhr?.exception || '';
          this.errors.oldStreetAddress.hasError = true;
          this.attachFieldErrors(this.oldAddressError, 'old');
        }
      }
      if (addressResponses?.xhr?.exception === 'InvalidPrimaryPoBoxException' && this.errors?.newPoBoxNumber?.hasError) {
        this.errors.newStreetAddress.hasError = false;
        this.errors.newStreetAddress.message = '';
      }
      if (addressResponses?.xhr?.fieldErrors && /new/g.test(addressResponses?.xhr?.fieldErrors[0].field)) {
        this.newAddressError = addressResponses?.xhr?.fieldErrors[0];
        this.attachFieldErrors(this.newAddressError, 'new');
      }
      if (addressResponses?.xhr?.fieldErrors && /old/g.test(addressResponses?.xhr?.fieldErrors[0].field)) {
        this.oldAddressError = addressResponses?.xhr?.fieldErrors[0];
        this.attachFieldErrors(this.oldAddressError, 'old');
      }
      if (addressResponses?.xhr?.fieldErrors) {
        // eslint-disable-next-line no-unused-expressions
        addressResponses?.xhr?.fieldErrors.forEach((fieldError) => {
          const fieldNameError = fieldError.field?.split('.')[1].charAt(0).toUpperCase() + fieldError.field?.split('.')[1].slice(1);
          const oldAddressField = `old${fieldNameError}`;
          const newAddressField = `new${fieldNameError}`;
          if (fieldError.field.indexOf('newAddress') === 0) {
            this.errors[newAddressField].message = fieldError.message;
            this.errors[newAddressField].hasError = true;
          } else {
            this.errors[oldAddressField].message = fieldError.message;
            this.errors[oldAddressField].hasError = true;
          }
        });
      }

      if (this.oldAddressError === 'OldAndNewAddressMatchException'
         || this.newAddressError === 'OldAndNewAddressMatchException') {
        this.newAddressError = 'OldAndNewAddressMatchException';
        this.oldAddressError = 'OldAndNewAddressMatchException';
      }

      if (addressResponses?.raw?.status >= 400 && (addressResponses?.raw?.status !== 420 && addressResponses?.raw?.status !== 422)) {
        this.fieldErrors = [
          ...this.fieldErrors,
          { field: 'oldStreetAddress', message: 'We are unable to validate this address at this time, please try again later.' },
          { field: 'newStreetAddress', message: 'We are unable to validate this address at this time, please try again later.' },
        ];
      }

      // log the defaultExceptions for old/new address
      if (!addressResponses?.xhr?.fieldErrors) {
        if (this.oldAddressError) {
          this.defaultException(this.oldAddressError);
        }
        if (this.newAddressError) {
          this.defaultException(this.newAddressError);
        }
      }
      const errorPageRedirect = this.evaluateErrorRedirect(addressResponses);
      if (errorPageRedirect) {
        this.$store.commit('DISABLE_LOADER');
        this.$router.push({ name: errorPageRedirect });
        return;
      }

      if (!addressResponses?.NEW?.uuid) {
        this.newConstructionEvaluateAddress(addressResponses);
      }

      if (this.newConstructionAttempts > 0) {
        this.$store.commit('DISABLE_LOADER');
        return;
      }

      // coaCheckHandle after the old/newAddress response
      const coaCheckHandle = await this.$coa.coaCheck();

      // Throw coaCheckModal if 'CoaCheckException'
      if (coaCheckHandle?.error) {
        const { exception } = coaCheckHandle.xhr;
        if (exception === 'CoaCheckException') {
          this.coaCheckModal = true;
          this.defaultException('DUPCOAEXCEPTION');
          this.$store.commit('DISABLE_LOADER');
          return;
        }
      }

      // log the amsStatusCodes on 200 responses for old/new addresses
      if (addressResponses?.addressType
         && this.isAmsMatch(addressResponses?.amsStatusCode)) {
        this.defaultException(addressResponses.amsStatusCode);
      }

      if (this.flagAmpersendEnabled) {
        this.$amp.identify({
          mgoMoverType: this.moverType,
          mgoForwardType: this.forwardType,
          mgoOldZip5: this.oldZipCode,
          mgoNewZip5: this.newZipCode,
          offersOptIn: JSON.parse(sessionStorage.getItem('mmco')) ?? this.offersChoice,
          voterregOptIn: JSON.parse(sessionStorage.getItem('mmcvr')) ?? this.autofillChoice,
        });
      }

      // Add Email domain start/stop banner flags to query string if API updated start/stop dates
      // Pass exception from response in case errors are cleared from a double submit
      this.next({
        ...this.$route.query,
        ...(this.medUpdated && { medUpdated: this.medUpdated }),
        ...(this.sdUpdated && { sdUpdated: this.sdUpdated }),
      }, addressResponses?.xhr?.exception);
    },
  },
  validateConfirmEmail: true,
};
</script>
<style>
@import '../../css/components/choice-selections.css';
#optin-mail-remainder:checked {
  accent-color: white;
  border: 1px solid black;
}

</style>
